import type { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetStartQuestionCourse } from '../../domain/hooks/use_question_course';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { UserInterface } from '../../../../core/components/user/domain/interfaces/user_interface';
import { CareerStatusEnum } from '../../../../core/enum/career_status_enum';
import ArrowBackIcon from '../../../../assets/images/arrow_back_black_icon.svg?react';
import UvibesIcon from '../../../../assets/images/uvibes_icon.svg?react';
import ThemeConstants from '../../../../config/theme/theme_constants';
import { AnimatePresence, motion } from 'framer-motion';
import StartQuizzQuestion from '../components/start_quizz_question';
import { useGetUserStartAnswers } from '../../domain/hooks/use_get_user_answers';
import { useStartTheme } from '../../domain/hooks/use_start_theme';
import { AnswerInterface } from '../../domain/interface/answer_interface';
import { useQueryClient } from '@tanstack/react-query';
import { SyncLoader } from 'react-spinners';
import { getCurrentUserTheme, getUserThemeByThemeId } from '../../domain/utils/get_current_user_theme';
import { useTranslation } from '../../../../i18n/useTranslation';

const theme = ThemeConstants();
const colors = [theme.UvibesYellow, theme.UvibesBlue, theme.UvibesOrange, theme.UvibesGreen];
const getCurrentColor = (index: number) => colors[index % colors.length];

const PAGE_VARIANTS = {
    enter: (direction: number) => ({
        x: direction > 0 ? window.innerWidth : -(window.innerWidth),
    }),
    center: { x: 0 },
    exit: (direction: number) => ({
        x: direction < 0 ? window.innerWidth : -(window.innerWidth),
    })
};

const PAGE_TRANSITION = {
    type: "tween" as const,
    duration: 0.2,
    ease: "easeInOut" as const,
};

const StartQuizzPage: React.FC = () => {
  const { t } = useTranslation();
  const { themeId, questionId } = useParams();
  const navigate = useNavigate();
  const { careerStatus, course_theme, company } = useOutletContext<UserInterface>();
  const { data: questions, isPending: isFetchingQuestions } = useGetStartQuestionCourse();
  const { data: answers, isPending: isFetchingAnswers } = useGetUserStartAnswers(themeId);
  const { mutate: startTheme, isPending: isStartingTheme } = useStartTheme();
  const queryClient = useQueryClient();
  const hasSubmittedRef = useRef(false);
  const prevQuestionIdRef = useRef<string | undefined>(undefined);

  const [currentAnswers, setCurrentAnswers] = useState<AnswerInterface[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const allAnswers = useMemo(
    () => [...(answers ?? []), ...currentAnswers],
    [answers, currentAnswers]
  );

  const currentIndex = useMemo(() => {
    if (!questions || questions.length === 0) return undefined;
    if (questionId === undefined) return 0;
    const index = questions.findIndex(q => q._id === questionId);
    return index !== -1 ? index : 0;
  }, [questions, questionId]);

  const alreadyStartedThisTheme = useMemo(
    () => !!getUserThemeByThemeId(course_theme, themeId, company?.id),
    [course_theme, themeId, company?.id]
  );

  const direction = useMemo(() => {
    if (!questions || !prevQuestionIdRef.current) return 1;
    const prevIdx = questions.findIndex(q => q._id === prevQuestionIdRef.current);
    const newIdx = questions.findIndex(q => q._id === questionId);
    if (prevIdx === -1 || newIdx === -1) return 1;
    return newIdx >= prevIdx ? 1 : -1;
  }, [questionId, questions]);

  useEffect(() => {
    prevQuestionIdRef.current = questionId;
  });

  useEffect(() => {
    if (!questions || questions.length === 0 || !themeId) return;
    const valid = questionId && questions.some(q => q._id === questionId);
    if (!valid) {
      navigate(`/career/${themeId}/quizz/${questions[0]._id}`, { replace: true });
    }
  }, [navigate, questionId, questions, themeId]);

  useEffect(() => {
    if (isFetchingAnswers || isFetchingQuestions) return;
    if (!questions || questions.length === 0 || !themeId || currentIndex === undefined) return;
    const firstUnansweredIdx = questions.findIndex(q => !allAnswers.some(a => a._questionId === q._id));
    if (firstUnansweredIdx === -1) return;
    if (currentIndex > firstUnansweredIdx) {
      navigate(`/career/${themeId}/quizz/${questions[firstUnansweredIdx]._id}`, { replace: true });
    }
  }, [questions, allAnswers, currentIndex, themeId, navigate, isFetchingAnswers, isFetchingQuestions]);

  useEffect(() => {
    if (
      hasSubmittedRef.current ||
      isStartingTheme ||
      submitError ||
      !themeId ||
      !questions ||
      questions.length === 0 ||
      allAnswers.length !== questions.length ||
      alreadyStartedThisTheme
    ) {
      return;
    }
    hasSubmittedRef.current = true;
    startTheme(
      { _themeId: themeId, answers: currentAnswers },
      {
        onSuccess: async () => {
          await queryClient.refetchQueries({ queryKey: ['userProfile'] });
          navigate(`/career/${themeId}/resources`, { replace: true });
        },
        onError: (err) => {
          const data = (err as AxiosError<{ message?: string }> | undefined)?.response?.data;
          setSubmitError(data?.message || t('course.quiz.save_error'));
        }
      }
    );
  }, [navigate, allAnswers, currentAnswers, questions, themeId, startTheme, queryClient, isStartingTheme, alreadyStartedThisTheme, submitError, t]);

  const retrySubmit = useCallback(() => {
    setSubmitError(null);
    hasSubmittedRef.current = false;
  }, []);

  useEffect(() => {
    const currentTheme = getCurrentUserTheme(course_theme, company?.id);
    if (careerStatus === CareerStatusEnum.Start) {
      toast.error(t('course.quiz.must_start'));
      navigate('/career/start', { replace: true });
    } else if (currentTheme && !alreadyStartedThisTheme) {
      toast.error(t('course.quiz.already_running'));
      const currentThemeId = typeof currentTheme._themeId === 'string' ? currentTheme._themeId : currentTheme._themeId!._id;
      navigate(`/career/${currentThemeId}/resources`, { replace: true });
    } else if (company?.permissions.course === false) {
      toast.error(t('course.quiz.no_permission'));
      navigate('/career/start', { replace: true });
    }
  }, [course_theme, careerStatus, navigate, company?.id, company?.permissions.course, themeId, alreadyStartedThisTheme, t]);

  const goToPrevPage = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goToNextPage = useCallback((answer?: AnswerInterface) => {
    if (!questions || currentIndex === undefined || !themeId) return;
    if (answer) {
      setCurrentAnswers(prev => {
        const existingIndex = prev.findIndex(a => a._questionId === answer._questionId);
        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = answer;
          return next;
        }
        return [...prev, answer];
      });
    }
    const newIndex = currentIndex + 1;
    if (newIndex >= questions.length) {
      if (alreadyStartedThisTheme) {
        navigate(`/career/${themeId}/resources`);
      }
      return;
    }
    navigate(`/career/${themeId}/quizz/${questions[newIndex]._id}`);
  }, [navigate, currentIndex, themeId, questions, alreadyStartedThisTheme]);

  const currentColor = getCurrentColor(currentIndex || 0);

  return (
    <div className="min-h-dvh py-[5.6dvh] max-lg:px-[3%] lg:px-[1%] flex flex-col gap-9 items-center relative overflow-x-hidden">
      <UvibesIcon className='fixed top-1/2 left-1/2 translate-y-[-54%] -translate-x-1/2 w-160 h-auto -z-30' color={`${currentColor}10`}/>
      <div className='flex flex-col gap-6 w-full items-center'>
        <div className='flex items-center w-full justify-between'>
          <button className='cursor-pointer self-start w-6 h-6 flex items-center justify-center my-1' onClick={goToPrevPage}>
            <ArrowBackIcon color='black' className='w-4.25 h-auto'/>
          </button>
          <h1 className=" text-2xl font-bold">{t('course.quiz.self_title')}</h1>
          <div/>
        </div>
        <p className="text-sm">
          {t('course.quiz.self_intro')}
        </p>
      </div>

      {!isFetchingAnswers && !isFetchingQuestions && currentIndex !== undefined && <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={PAGE_VARIANTS}
          initial="enter"
          animate="center"
          exit="exit"
          transition={PAGE_TRANSITION}
          className="w-full flex justify-center"
        >
          {questions && questions.length > 0 && (
            <StartQuizzQuestion
              question={questions[currentIndex]}
              color={currentColor}
              index={currentIndex}
              questionsLength={questions.length}
              onNext={goToNextPage}
              isPending={isStartingTheme}
              answer={currentAnswers.find(a => a._questionId === questions[currentIndex]._id) ?? answers?.find(a => a._questionId === questions[currentIndex]._id)}
            />
          )}
        </motion.div>
      </AnimatePresence>}
      {(isFetchingAnswers || isFetchingQuestions) &&
        <div className='flex flex-col w-full items-center justify-center min-h-90'>
          <SyncLoader color={currentColor} size={24} speedMultiplier={0.5}/>
        </div>
      }
      {submitError && (
        <div className="flex flex-col w-full items-center justify-center gap-3 px-6 text-center">
          <span className="text-sm font-medium">{submitError}</span>
          <button
            className="px-4 py-2 rounded-[18px] text-white font-semibold text-sm cursor-pointer disabled:opacity-60"
            style={{ background: theme.UvibesPink }}
            onClick={retrySubmit}
            disabled={isStartingTheme}
          >
            {t('common.button.retry')}
          </button>
        </div>
      )}

    </div>
  );
};

export default StartQuizzPage;
